// /src/components/core/IssuesDropdown.jsx
// Dropdown component for platform admins (users.is_sys_admin) to view and manage error reports
// Shows unacknowledged issues — copy-to-clipboard for Nextcloud Deck, and dismiss/dismiss-all
// to acknowledge (this dropdown's own acknowledged_at marker, independent of the Deck-sync and
// digest jobs' own delivery markers)

import { useState, useEffect } from 'react';
import { X, Copy, Check, AlertCircle } from 'lucide-react';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import { formatTimeAgo } from '../../utils/formatText';
import { logAdminAction, auditFailureMessage } from '../../utils/adminAuditLog';
import ConfirmationModal from './ConfirmationModal';
import StandardButton from './StandardButton';

const TYPE_BADGES = {
  bug: { labelKey: 'type_bug', bg: '#FEE2E2', color: '#991B1B' },
  suggestion: { labelKey: 'type_suggestion', bg: '#DBEAFE', color: '#1E40AF' },
  question: { labelKey: 'type_question', bg: '#FEF3C7', color: '#92400E' },
  other: { labelKey: 'type_other', bg: '#F3F4F6', color: '#374151' }
};

function IssuesDropdown({ isOpen, onClose, issueCount, onIssueCountChange }) {
  const { t, formattingLocale } = useTranslation('admin');
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [infoModal, setInfoModal] = useState({ visible: false, message: '' });
  const showInfo = (message) => setInfoModal({ visible: true, message });
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadIssues();
    }
  }, [isOpen]);

  const loadIssues = async () => {
    try {
      setLoading(true);

      // Reports the human hasn't acknowledged yet — its own marker, independent of whether the
      // Deck-sync/digest jobs have processed this report via their own delivery markers.
      const { data, error } = await supabaseFetch.query('error_reports', {
        select: '*',
        is: { acknowledged_at: null },
        order: 'created_at.desc',
        limit: 50
      });

      if (error) {
        console.error('Error loading issues:', error);
        return;
      }

      setIssues(data || []);

      if (onIssueCountChange && data?.length !== issueCount) {
        onIssueCountChange(data?.length || 0);
      }
    } catch (err) {
      console.error('Error loading issues:', err);
    } finally {
      setLoading(false);
    }
  };

  // NOTE: formatReportCard generates markdown for admin/developer use (Nextcloud Deck)
  // and intentionally remains in English (not translated) — its date too, so cards read the same
  // whichever language the admin who copied them was using (#85: the only fixed en-GB left here).
  const formatReportCard = (issue) => {
    const date = new Date(issue.created_at).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const reportType = (issue.report_type || 'bug').toUpperCase();

    // Parse debug context if available
    let debugInfo = '';
    if (issue.debug_context) {
      try {
        const debug = typeof issue.debug_context === 'string'
          ? JSON.parse(issue.debug_context)
          : issue.debug_context;

        const debugLines = [];
        if (debug.component) debugLines.push(`**Component:** ${debug.component}`);
        if (debug.selectedArea) debugLines.push(`**Selected Area:** ${debug.selectedArea}`);
        if (debug.selectedLocality) debugLines.push(`**Selected Locality:** ${debug.selectedLocality}`);
        if (debug.groupId) debugLines.push(`**Group ID:** ${debug.groupId}`);
        if (debug.itemId) debugLines.push(`**Item ID:** ${debug.itemId} (${debug.itemType || 'item'})`);
        if (debug.pathname) debugLines.push(`**Path:** ${debug.pathname}`);

        // Include recent errors
        if (debug.recentErrors?.length) {
          debugLines.push(`**JS Errors (${debug.recentErrors.length}):**`);
          debug.recentErrors.forEach(err => {
            debugLines.push(`  - ${err.message} (${err.source || ''}:${err.line || ''})`);
          });
        }

        // Include symptoms
        if (debug.selectedSymptoms?.length) {
          debugLines.push(`**Symptoms:** ${debug.selectedSymptoms.map(s => s.replace(/-/g, ' ')).join(', ')}`);
        }

        // Include breadcrumbs
        if (debug.breadcrumbs?.length) {
          debugLines.push(`**Activity Trail (${debug.breadcrumbs.length}):**`);
          debug.breadcrumbs.forEach(bc => {
            if (bc.type === 'navigate') {
              debugLines.push(`  - → ${bc.view}${bc.itemId ? ' #' + bc.itemId.substring(0, 8) : ''}`);
            } else if (bc.type === 'area-changed') {
              debugLines.push(`  - Area: ${bc.displayName || bc.areaCode}`);
            } else if (bc.type === 'form-submit') {
              debugLines.push(`  - Submit: ${bc.action}${bc.component ? ' (' + bc.component + ')' : ''}`);
            } else if (bc.type === 'auth') {
              debugLines.push(`  - Auth: ${bc.action}`);
            } else if (bc.type === 'action') {
              debugLines.push(`  - Action: ${bc.action}${bc.component ? ' (' + bc.component + ')' : ''}`);
            } else {
              debugLines.push(`  - ${bc.type}: ${JSON.stringify(bc)}`);
            }
          });
        }

        if (debugLines.length > 0) {
          debugInfo = `\n### Debug Context\n${debugLines.join('\n')}\n`;
        }
      } catch (e) {
        console.warn('Failed to parse debug_context:', e);
      }
    }

    return `## [${reportType}] Issue Report
**Date:** ${date}
**View:** ${issue.current_view || 'Unknown'}
**Context:** ${issue.current_context || 'Unknown'} (${issue.context_type || 'Unknown'})
**Viewport:** ${issue.viewport || 'Unknown'}
${issue.user_id ? `**User ID:** ${issue.user_id}\n` : ''}${issue.user_email ? `**Email:** ${issue.user_email}\n` : ''}${debugInfo}
### User Feedback
${issue.user_feedback}

---
*Report ID: ${issue.id.slice(0, 8)}*`;
  };

  // Parse debug context for display
  const parseDebugContext = (issue) => {
    if (!issue.debug_context) return null;
    try {
      return typeof issue.debug_context === 'string'
        ? JSON.parse(issue.debug_context)
        : issue.debug_context;
    } catch {
      return null;
    }
  };

  // Clipboard convenience only — does NOT acknowledge the report or remove it from the list.
  // Copying for a manual Nextcloud paste and dismissing the badge are separate, honest actions
  // now (previously Copy did both unconditionally, even when the clipboard write failed).
  const handleCopy = async (issue) => {
    const reportCard = formatReportCard(issue);
    let clipboardOk = false;
    try {
      await navigator.clipboard.writeText(reportCard);
      clipboardOk = true;
    } catch (err) {
      console.warn('Clipboard copy failed:', err);
    }

    setCopiedId(issue.id);
    setTimeout(() => setCopiedId(null), 2000);

    if (!clipboardOk) {
      showInfo(t('copy_failed_error'));
    }
  };

  // Explicit dismiss — this report's own acknowledged_at marker, independent of the Deck-sync
  // and digest jobs' own markers and of the report's status lifecycle.
  const handleDismiss = async (issue) => {
    const { error } = await supabaseFetch.update('error_reports', { acknowledged_at: new Date().toISOString() }, {
      eq: { id: issue.id }
    });

    if (error) {
      console.error('Error acknowledging issue:', error);
      showInfo(t('dismiss_failed_error'));
      return;
    }

    const audit = await logAdminAction({
      actionType: 'problem_report_acknowledged',
      targetType: 'error_report',
      targetId: issue.id
    });

    setIssues(prev => prev.filter(i => i.id !== issue.id));
    if (onIssueCountChange) {
      onIssueCountChange(prev => Math.max(0, prev - 1));
    }
    const auditMessage = auditFailureMessage(t, audit);
    if (auditMessage) showInfo(auditMessage);
  };

  const handleDismissAll = async () => {
    const ids = issues.map(i => i.id);
    if (ids.length === 0) return;

    const { error } = await supabaseFetch.update('error_reports', { acknowledged_at: new Date().toISOString() }, {
      inFilter: { id: ids }
    });

    if (error) {
      console.error('Error acknowledging all issues:', error);
      showInfo(t('dismiss_failed_error'));
      return;
    }

    const audits = await Promise.all(ids.map(id => logAdminAction({
      actionType: 'problem_report_acknowledged',
      targetType: 'error_report',
      targetId: id
    })));

    setIssues([]);
    if (onIssueCountChange) {
      onIssueCountChange(0);
    }
    // One message for the lot, with how many lines were lost.
    const auditMessage = auditFailureMessage(t, audits);
    if (auditMessage) showInfo(auditMessage);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.3)',
          zIndex: 9998,
          pointerEvents: 'auto'
        }}
      />

      {/* Dropdown Panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
        position: 'fixed',
        top: '60px',
        right: '20px',
        width: '400px',
        maxHeight: 'calc(100vh - 200px)',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pointerEvents: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#F9FAFB'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={20} color="#DC2626" />
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#1F2937'
            }}>
              {t('header_title', { count: issues.length })}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {issues.length > 0 && (
              <StandardButton
                variant="admin"
                size="pill"
                iconName="Check"
                label={t('dismiss_all_button')}
                onClick={handleDismissAll}
                title={t('dismiss_all_button')}
              />
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} color="#6B7280" />
            </button>
          </div>
        </div>

        {/* Issues List */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '8px'
        }}>
          {loading ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: '#6B7280'
            }}>
              {t('loading_message')}
            </div>
          ) : issues.length === 0 ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: '#6B7280'
            }}>
              <Check size={32} color="#10B981" style={{ marginBottom: '8px' }} />
              <div>{t('empty_state_message')}</div>
            </div>
          ) : (
            issues.map(issue => {
              const typeBadge = TYPE_BADGES[issue.report_type] || TYPE_BADGES.other;
              const typeBadgeLabel = t(typeBadge.labelKey);
              return (
                <div
                  key={issue.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    background: copiedId === issue.id ? '#D1FAE5' : '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    transition: 'background 0.3s ease'
                  }}
                >
                  {/* Issue Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexWrap: 'wrap'
                    }}>
                      {/* Report type badge */}
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: typeBadge.bg,
                        color: typeBadge.color
                      }}>
                        {typeBadgeLabel}
                      </span>
                      <span style={{ fontWeight: '600', color: '#374151' }}>
                        {issue.current_view || t('unknown_label')}
                      </span>
                      {' · '}
                      {issue.current_context || t('unknown_label')}
                      {' · '}
                      {formatTimeAgo(issue.created_at, t, formattingLocale)}
                    </div>

                    {/* Copy (clipboard convenience only) + Dismiss (acknowledges — separate,
                        honest actions; Copy no longer implies dismissal) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => handleCopy(issue)}
                        disabled={copiedId === issue.id}
                        style={{
                          background: copiedId === issue.id ? '#10B981' : '#024B99',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: copiedId === issue.id ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'background 0.2s ease'
                        }}
                      >
                        {copiedId === issue.id ? (
                          <>
                            <Check size={14} />
                            {t('copied_button')}
                          </>
                        ) : (
                          <>
                            <Copy size={14} />
                            {t('copy_button')}
                          </>
                        )}
                      </button>
                      <StandardButton
                        variant="admin"
                        size="circle"
                        iconName="Check"
                        onClick={() => handleDismiss(issue)}
                        title={t('dismiss_button')}
                      />
                    </div>
                  </div>

                  {/* Symptom Badges */}
                  {(() => {
                    const debug = parseDebugContext(issue);
                    if (!debug?.selectedSymptoms?.length) return null;
                    return (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                        {debug.selectedSymptoms.map(symptom => (
                          <span key={symptom} style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: '600',
                            background: '#FEE2E2',
                            color: '#991B1B'
                          }}>
                            {symptom.replace(/-/g, ' ')}
                          </span>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Issue Feedback */}
                  <div style={{
                    fontSize: '14px',
                    color: '#1F2937',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {issue.user_feedback}
                  </div>

                  {/* Debug Context (programmer info) */}
                  {(() => {
                    const debug = parseDebugContext(issue);
                    if (!debug) return null;
                    const hasErrors = debug.recentErrors?.length > 0;
                    return (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        background: hasErrors ? '#FEE2E2' : '#FEF3C7',
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: hasErrors ? '#991B1B' : '#92400E'
                      }}>
                        {debug.component && <div><strong>{t('debug_component_label')}</strong> {debug.component}</div>}
                        {debug.selectedArea && <div><strong>{t('debug_area_label')}</strong> {debug.selectedArea}</div>}
                        {debug.selectedLocality && <div><strong>{t('debug_locality_label')}</strong> {debug.selectedLocality}</div>}
                        {debug.groupId && <div><strong>{t('debug_group_label')}</strong> {debug.groupId}</div>}
                        {debug.itemId && <div><strong>{t('debug_item_label')}</strong> {debug.itemType || 'item'} #{debug.itemId}</div>}
                        {debug.pathname && <div><strong>{t('debug_path_label')}</strong> {debug.pathname}</div>}
                        {hasErrors && (
                          <div style={{ marginTop: '4px' }}>
                            <strong>{t('debug_js_errors_label', { count: debug.recentErrors.length })}</strong>
                            {debug.recentErrors.map((err, i) => (
                              <div key={i} style={{ paddingLeft: '8px', fontSize: '10px' }}>
                                {err.message} {err.source ? `(${err.source}:${err.line || ''})` : ''}
                              </div>
                            ))}
                          </div>
                        )}
                        {debug.breadcrumbs?.length > 0 && (
                          <div style={{ marginTop: '4px' }}>
                            <strong>{t('debug_trail_label', { count: debug.breadcrumbs.length })}</strong>
                            {debug.breadcrumbs.map((bc, i) => (
                              <span key={i} style={{ fontSize: '10px' }}>
                                {i > 0 ? ' → ' : ' '}
                                {bc.type === 'navigate' ? bc.view
                                  : bc.type === 'area-changed' ? `Area:${bc.displayName || bc.areaCode}`
                                  : bc.type === 'form-submit' ? <strong style={{ color: '#DC2626' }}>Submit:{bc.action}</strong>
                                  : bc.type === 'auth' ? <strong style={{ color: '#7C3AED' }}>Auth:{bc.action}</strong>
                                  : bc.type === 'action' ? <strong>{bc.action}</strong>
                                  : bc.type}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Footer: viewport + user info */}
                  <div style={{
                    marginTop: '8px',
                    fontSize: '11px',
                    color: '#9CA3AF',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>{issue.viewport || t('unknown_label')}</span>
                    {issue.user_id && (
                      <span title={issue.user_id}>User: {issue.user_id.substring(0, 8)}...</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        {issues.length > 0 && (
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #E5E7EB',
            background: '#F9FAFB',
            fontSize: '12px',
            color: '#6B7280',
            textAlign: 'center'
          }}>
            {t('footer_hint')}
          </div>
        )}
      </div>

      <ConfirmationModal
        visible={infoModal.visible}
        message={infoModal.message}
        onOk={() => setInfoModal({ visible: false, message: '' })}
        okLabel="OK"
      />
    </>
  );
}

export default IssuesDropdown;
